/* eslint-disable */
import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid,
  TablePagination,
  Link,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import { useSelector } from "react-redux";
import { formatSpeed, formatTime } from "../common/util/formatter";
import ReportFilter from "./components/ReportFilter";
import { prefixString } from "../common/util/stringUtils";
import { useTranslation } from "../common/components/LocalizationProvider";
import PageLayout from "../common/components/PageLayout";
import ReportsMenu from "./components/ReportsMenu";
import usePersistedState from "../common/util/usePersistedState";
import ColumnSelect from "./components/ColumnSelect";
import { useCatch, useEffectAsync } from "../reactHelper";
import useReportStyles from "./common/useReportStyles";
import TableShimmer from "../common/components/TableShimmer";
import { useAttributePreference } from "../common/util/preferences";
import MapView from "../map/core/MapView";
import MapGeofence from "../map/MapGeofence";
import MapPositions from "../map/MapPositions";
import MapCamera from "../map/MapCamera";

const columnsArray = [
  ["eventTime", "positionFixTime"],
  ["type", "sharedType"],
  ["geofenceId", "sharedGeofence"],
  ["attributes", "commandData"],
];
const columnsMap = new Map(columnsArray);

const GeofencesReportPage = () => {
  const classes = useReportStyles();
  const t = useTranslation();

  // Adicionando verificações de segurança para evitar erros com propriedades undefined
  const devices = useSelector((state) => state?.devices?.items || {});
  const geofences = useSelector((state) => state?.geofences?.items || {});

  const speedUnit = useAttributePreference("speedUnit");

  const [columns, setColumns] = usePersistedState("eventColumns", [
    "eventTime",
    "type",
    "geofenceId",
    "attributes",
  ]);
  const [eventTypes, setEventTypes] = useState(["geofenceEnter","geofenceExit","geofenceStay"]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [position, setPosition] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffectAsync(async () => {
    if (selectedItem) {
      try {
        const response = await fetch(
          `/api/positions?id=${selectedItem.positionId}`
        );
        if (response.ok) {
          const positions = await response.json();
          if (positions.length > 0) {
            setPosition(positions[0]);
          } else {
            setPosition(null);
          }
        } else {
          console.error("Error fetching position:", await response.text());
          setPosition(null);
        }
      } catch (error) {
        console.error("Error fetching position:", error);
        setPosition(null);
      }
    } else {
      setPosition(null);
    }
  }, [selectedItem]);

  const handleSubmit = useCatch(async ({ deviceId, from, to, type }) => {
    if (!deviceId || !from || !to) {
      return; // Validação básica
    }
    
    const query = new URLSearchParams({ deviceId, from, to, cost: 0 });
    eventTypes.forEach((it) => query.append("type", it));
    
    if (type === "export") {
      window.location.assign(`/api/reports/events/xlsx?${query.toString()}`);
    } else if (type === "mail") {
      try {
        const response = await fetch(
          `/api/reports/events/mail?${query.toString()}`
        );
        if (!response.ok) {
          throw Error(await response.text());
        }
      } catch (error) {
        console.error("Error sending mail:", error);
      }
    } else {
      setLoading(true);
      try {
        const response = await fetch(
          `/api/reports/events?${query.toString()}`,
          {
            headers: { Accept: "application/json" },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setItems(data || []);
        } else {
          console.error("Error fetching events:", await response.text());
          setItems([]);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    }
  });

  const formatValue = (item, key) => {
    if (!item) return "";
    
    switch (key) {
      case "eventTime":
        return item[key] ? formatTime(item[key]) : "";
      case "type":
        return item[key] ? t(prefixString("event", item[key])) : "";
      case "geofenceId":
        if (item[key] > 0) {
          const geofence = geofences[item[key]];
          return geofence && geofence.name;
        }
        return null;
      case "attributes":
        if (!item.attributes || !item.type) return "";
        
        switch (item.type) {
          case "alarm":
            return item.attributes.alarm ? t(prefixString("alarm", item.attributes.alarm)) : "";
          case "deviceOverspeed":
            return item.attributes.speed !== undefined ? formatSpeed(item.attributes.speed, speedUnit, t) : "";
          case "driverChanged":
            return item.attributes.driverUniqueId || "";
          case "sendCommand":
            return item.attributes.command ? t(prefixString("command", item.attributes.command)) : "";
          case "media":
            if (item.attributes.file && devices[item.deviceId]?.uniqueId) {
              return (
                <Link
                  href={`/api/media/${devices[item.deviceId].uniqueId}/${item.attributes.file}`}
                  target="_blank"
                >
                  {item.attributes.file}
                </Link>
              );
            }
            return "";
          case "commandResult":
            return item.attributes.result || "";
          default:
            return "";
        }
      default:
        return item[key] || "";
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, items.length - page * rowsPerPage);

  // Conteúdo principal
  const pageContent = (
    <div className={classes.container}>
      {selectedItem && (
        <div className={classes.containerMap}>
          <MapView>
            <MapGeofence />
            {position && <MapPositions positions={[position]} />}
          </MapView>
          {position && (
            <MapCamera
              latitude={position.latitude}
              longitude={position.longitude}
            />
          )}
        </div>
      )}
      <div className={classes.containerMain}>
        <div className={classes.header}>
          <Accordion defaultExpanded style={{ marginTop: 8 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">{t("sharedFilter")}</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <ReportFilter 
                handleSubmit={handleSubmit}
                handleSchedule={undefined}
                showOnly={undefined}
                ignoreDevice={undefined}
                multiDevice={undefined}
                includeGroups={undefined}
                loading={loading}
              >
                <Grid item xs={12} sm={12}>
                  <ColumnSelect
                    columns={columns}
                    setColumns={setColumns}
                    columnsArray={columnsArray}
                    rawValues={true}
                    disabled={loading}
                  />
                </Grid>
              </ReportFilter>
            </AccordionDetails>
          </Accordion>
        </div>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={classes.columnAction} />
              {columns.map((key) => (
                <TableCell key={key}>{t(columnsMap.get(key) || key)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading ? (
              items.length > 0 ? (
                items
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow key={item.id || Math.random()}>
                      <TableCell
                        className={classes.columnAction}
                        padding="none"
                      >
                        {item.positionId ? (
                          selectedItem === item ? (
                            <IconButton
                              size="small"
                              onClick={() => setSelectedItem(null)}
                            >
                              <GpsFixedIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => setSelectedItem(item)}
                            >
                              <LocationSearchingIcon fontSize="small" />
                            </IconButton>
                          )
                        ) : (
                          ""
                        )}
                      </TableCell>
                      {columns.map((key) => (
                        <TableCell key={key}>
                          {formatValue(item, key)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
              ) : (
                <TableRow style={{ height: 72 * 5 }}>
                  <TableCell align="center" colSpan={columns.length + 1}>
                    {t("sharedRowsEmpty")}
                  </TableCell>
                </TableRow>
              )
            ) : (
              <TableShimmer 
                columns={columns.length + 1} 
                startAction={true}
                endAction={false}
              />
            )}
            {/* Linhas vazias para paginação */}
            {emptyRows > 0 && items.length > 0 && (
              <TableRow style={{ height: 72 * emptyRows }}>
                <TableCell align="center" colSpan={columns.length + 1} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        {/* Paginação - Mostrar apenas se tivermos dados */}
        {items.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[
              5,
              10,
              25,
              { label: t("sharedAll"), value: -1 },
            ]}
            component="div"
            count={items.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            SelectProps={{
              inputProps: { "aria-label": t("sharedRowsPerPage") },
              native: true,
            }}
          />
        )}
      </div>
    </div>
  );

  return (
    <PageLayout
      menu={<ReportsMenu />}
      breadcrumbs={["reportTitle", "sharedGeofences"]}
    >
      {pageContent}
    </PageLayout>
  );
};

export default GeofencesReportPage;